/**
 * ChatInner.tsx
 *
 * Main chat inner container component.
 * Refactored from ChatInner.v2.tsx (~1447 lines) to use extracted components and hooks.
 */

import { FC, useEffect, useRef, useState, useCallback } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

// Redux
import { RootState } from "src/setup/redux/store";
import { fetchOlderMessagesForRoom } from "src/app/modules/chat/redux/ChatThunk";

// Components
import ChatMessageList from "./ChatMessageList";
import ChatInputArea from "./ChatInputArea";
import ChatContactHeader from "../chat-wrapper/ChatContactHeader";
import { MediaModalProvider } from "../../context/MediaModalContext";
import { MediaModal } from "../media-modal/MediaModal";
import { DataUsageWarningModal } from "./DataUsageWarningModal";

// Hooks
import { useFileUpload } from "../../hooks/useFileUpload";
import { useMessageSender } from "../../hooks/useMessageSender";
import { useUploadWithProgress } from "../../hooks/useUploadWithProgress";
import { useNetworkDetection } from "../../hooks/useNetworkDetection";
import { useBatteryStatus } from "../../hooks/useBatteryStatus";

// Database
import { getAccess } from "src/db/serviceApiSetting";

// Models
import Account from "src/app/models/Account";
import Customer from "src/app/models/Customer";
import { Collaboration, Room } from "src/app/models/Chat";

// Config
import { env } from "src/config/env";

// Utils
import { shouldWarnForCellularUpload } from "../../utils/networkHelpers";
import { fetchCachedAccountById } from "../../services/chatEntityStorageCache";

// Styles
import "src/styles/css/chat.scss";
import "src/styles/css/dropdown.scss";
import "src/styles/css/margin.scss";

const getMediaUploadTimeoutMs = (fileSize: number): number => {
  const sizeMb = fileSize / (1024 * 1024);
  const timeoutMs = 120000 + Math.ceil(sizeMb) * 30000;
  return Math.min(timeoutMs, 300000);
};

export interface ChatInnerProps {
  collaboration: Collaboration | undefined;
  selectedRoom?: Room;
  contactData?: Customer;
  activeTab: "allChat" | "myChat";
  onBackClick?: () => void;
  onAddNewContact: () => void;
  onAddExistingContact: () => void;
  isMobile?: boolean;
}

const ChatInner: FC<ChatInnerProps> = ({
  collaboration: selectedCollaboration,
  selectedRoom,
  contactData,
  activeTab: activeTabProp,
  onBackClick,
  onAddNewContact,
  onAddExistingContact,
  isMobile = false,
}) => {
  const dispatch = useDispatch();

  // Refs
  const chatBodyRef = useRef<HTMLDivElement>(null);
  const hasInitiallyScrolledRef = useRef<boolean>(false);
  const isUserAtBottomRef = useRef<boolean>(true);
  const isLoadingOlderMessagesRef = useRef<boolean>(false);
  const previousScrollHeightRef = useRef<number>(0);
  const previousMessageCountRef = useRef<number>(0);
  const previousRoomIdRef = useRef<string | null>(null);

  // Redux selectors
  const { replyToMessage, orderInitiateMessage, chatInput } = useSelector(
    (state: RootState) => state.Chat
  );

  // Use activeTab from props, fall back to Redux if not provided
  const activeTabFromRedux = useSelector(
    (state: RootState) => state.ChatV2.activeTab,
    shallowEqual
  );
  const activeTab = activeTabProp || activeTabFromRedux;

  // Get selected collaboration and room IDs for message fetching
  const selectedCollaborationId = selectedCollaboration?.id || null;
  const selectedRoomId = selectedRoom?.id || null;
  const roomAccountData = selectedRoom?.accountData as
    | { marketplaceShopId?: string }
    | undefined;

  const messages = useSelector(
    (state: RootState) =>
      state.ChatV2.messagesByRoom[
        `${selectedCollaborationId}_${selectedRoomId}`
      ]?.messages ?? [],
    shallowEqual
  );

  const lastVisible = useSelector(
    (state: RootState) =>
      state.ChatV2.messagesByRoom[
        `${selectedCollaborationId}_${selectedRoomId}`
      ]?.lastVisible ?? null
  );

  const hasMore = useSelector(
    (state: RootState) =>
      state.ChatV2.messagesByRoom[
        `${selectedCollaborationId}_${selectedRoomId}`
      ]?.hasMore ?? true
  );

  const isLoading = useSelector(
    (state: RootState) =>
      state.ChatV2.messagesByRoom[
        `${selectedCollaborationId}_${selectedRoomId}`
      ]?.isLoading ?? false
  );

  // Local state
  const [message, setMessage] = useState<string>("");
  const [showScrollBottom, setShowScrollBottom] = useState(false);
  const [debug, setDebug] = useState<boolean>(false);
  const [account, setAccount] = useState<Account | undefined>(undefined);

  // Upload progress state
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [isSendingToPlatform, setIsSendingToPlatform] = useState<boolean>(false);
  const [estimatedUploadTime, setEstimatedUploadTime] = useState<string | undefined>(undefined);
  const [sendError, setSendError] = useState<string | undefined>(undefined);

  // Data usage warning state
  const [showDataWarning, setShowDataWarning] = useState<boolean>(false);
  const [pendingUpload, setPendingUpload] = useState<(() => Promise<{ url?: string; id?: string } | undefined>) | null>(null);

  // Mobile detection hooks
  const { connectionType, isCellular } = useNetworkDetection();
  const { isBatterySaverLikely } = useBatteryStatus();

  // Fetch account when room changes (for marketplace shop ID)
  useEffect(() => {
    const fetchAccount = async () => {
      if (selectedRoom?.account?.id) {
        try {
          const fetchedAccount = (await fetchCachedAccountById(
            selectedRoom.account.id,
            {
              source: "chat_inner_room_change",
              collaborationId: selectedCollaborationId ?? undefined,
              roomId: selectedRoomId ?? undefined,
            }
          )) as Account;
          setAccount(fetchedAccount);
        } catch (error) {
          console.error('[ChatInner] Failed to fetch account:', error);
          setAccount(undefined);
        }
      } else {
        setAccount(undefined);
      }
    };

    fetchAccount();
  }, [selectedRoom?.account?.id]);

  // Custom hooks for file upload and message sending
  const {
    file,
    fileSize,
    inputFileName,
    messageType,
    generatedThumbnail,
    isHEIC,
    setFile,
    setFileSize,
    setInputFileName,
    setMessageType,
    setGeneratedThumbnail,
    uploadFile,
    uploadMediaFallback,
    handleFileSelect,
    removeFile,
  } = useFileUpload({
    selectedRoom,
    selectedCollaboration,
    account,
  });

  // Upload progress hook
  const { uploadFile: uploadFileWithProgress, abort: abortUpload } = useUploadWithProgress();

  /**
   * Perform the actual upload (internal function)
   */
  const performUpload = useCallback(async (): Promise<
    { url?: string; id?: string } | undefined
  > => {
    if (!file) {
      console.warn('[ChatInner] performUpload: No file to upload');
      return undefined;
    }

    // Build endpoint
    const channel = selectedRoom?.channel;
    if (!channel) return undefined;

    const platform = channel.toLowerCase();
    let sender: string | null = null;

    switch (platform) {
      case 'whatsapp':
        sender = selectedRoom?.userSearchKey || selectedCollaboration?.phoneNumber || null;
        break;
      case 'shopee':
      case 'lazada':
      case 'tiktok':
        sender = account?.marketplaceShopId || null;
        break;
      default:
        sender = null;
    }

    const endpoint = sender ? `/v2/${platform}/${sender}/media` : null;
    if (!endpoint) {
      console.error('[ChatInner] Could not determine media upload endpoint');
      return undefined;
    }

    try {
      // Get access token
      const apiAccess = await getAccess();
      if (!apiAccess?.accessToken) {
        console.error('[ChatInner] No access token found');
        return undefined;
      }

      const fullUrl = `${env.SERVER_URL}${endpoint}`;

      // Set uploading state
      setIsUploading(true);
      setUploadProgress(0);
      setSendError(undefined);

      // Call upload with progress tracking
      const result = await uploadFileWithProgress(
        file,
        fullUrl,
        (percent) => {
          setUploadProgress(percent);

          // Transition to "sending" state immediately when upload bytes reach 100%
          // (before waiting for server response)
          if (percent >= 100) {
            setIsUploading(false);
            setIsSendingToPlatform(true);
            console.log('[ChatInner] Upload bytes complete, now sending to platform...');
          }

          // TODO: Calculate estimated time based on upload speed
        },
        {
          headers: {
            Authorization: `Bearer ${apiAccess.accessToken}`,
          },
          additionalFormData: {
            messaging_product: platform,
            type: file.type.split('/')[0], // image, video, audio, document
          },
          timeoutMs: getMediaUploadTimeoutMs(file.size),
        }
      );

      // Server response received
      if (!result.success) {
        // Reset states on error and surface the error message to the UI
        setIsUploading(false);
        setIsSendingToPlatform(false);
        setUploadProgress(0);
        setEstimatedUploadTime(undefined);
        setSendError(result.error || 'Upload failed. Try again');
        console.error('[ChatInner] Upload failed:', result.error);
        return undefined;
      }

      console.log('[ChatInner] Server response received, media ready');

      return {
        url: result.mediaUrl,
        id: result.uploadId,
      };
    } catch (error) {
      console.error('[ChatInner] Upload error:', error);
      setIsUploading(false);
      setIsSendingToPlatform(false);
      setUploadProgress(0);
      setEstimatedUploadTime(undefined);
      setSendError(
        error instanceof Error && error.message
          ? error.message
          : 'Upload failed. Try again'
      );
      return undefined;
    }
  }, [
    file,
    selectedRoom,
    selectedCollaboration,
    account,
    uploadFileWithProgress,
  ]);

  /**
   * Custom upload function with progress tracking and data usage warning
   * Replaces the standard uploadMediaFallback to provide real-time progress updates
   * Checks for cellular data and file size before proceeding
   */
  const uploadMediaWithProgress = useCallback(async (): Promise<
    { url?: string; id?: string } | undefined
  > => {
    if (!file) {
      console.warn('[ChatInner] uploadMediaWithProgress: No file to upload');
      return undefined;
    }

    // Check for cellular + large file (>3MB) warning
    if (shouldWarnForCellularUpload(file.size, connectionType)) {
      console.log('[ChatInner] Large file on cellular data detected, showing warning');
      setPendingUpload(() => performUpload);
      setShowDataWarning(true);
      // Return undefined - upload will be triggered after user confirms
      return undefined;
    }

    // No warning needed, proceed with upload
    return performUpload();
  }, [file, connectionType, performUpload]);

  const { isSending, setIsSending, sendMessage: sendMessageHook } = useMessageSender({
    selectedCollaboration,
    selectedRoom,
    replyToMessage,
    orderInitiateMessage,
    file,
    inputFileName,
    fileSize,
    messageType,
    uploadFile,
    uploadMediaFallback: uploadMediaWithProgress,
    generatedThumbnail,
  });

  // Wrapper functions for hook methods
  const clearMessageState = useCallback(() => {
    setMessage("");
    setFile(undefined);
    setFileSize("");
    setInputFileName("");
    setMessageType("text");
    setGeneratedThumbnail(null);
  }, [
    setFile,
    setFileSize,
    setInputFileName,
    setMessageType,
    setGeneratedThumbnail,
  ]);

  const sendMessage = useCallback(
    async (templateLanguage?: any) => {
      await sendMessageHook(message, clearMessageState, templateLanguage);
    },
    [sendMessageHook, message, clearMessageState]
  );

  const onEnterPress = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.keyCode === 13 && !e.shiftKey && message.trim() !== "") {
        e.preventDefault();
        sendMessage();
      }
    },
    [message, sendMessage]
  );

  const handleRemoveFile = useCallback(() => {
    setMessage("");
    removeFile();
  }, [removeFile]);

  const handleCancelUpload = useCallback(() => {
    abortUpload();
    setIsUploading(false);
    setIsSendingToPlatform(false);
    setUploadProgress(0);
    setEstimatedUploadTime(undefined);
  }, [abortUpload]);

  /**
   * Handle data warning modal continue - proceed with upload
   */
  const handleDataWarningContinue = useCallback(async () => {
    setShowDataWarning(false);
    if (pendingUpload) {
      // Execute the pending upload
      await pendingUpload();
      setPendingUpload(null);
    }
  }, [pendingUpload]);

  /**
   * Handle data warning modal cancel - abort upload
   */
  const handleDataWarningCancel = useCallback(() => {
    setShowDataWarning(false);
    setPendingUpload(null);
    // Don't clear the file - user might want to wait for WiFi
  }, []);

  // Scroll handler
  const handleScroll = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;

      const isAtBottom = scrollHeight - scrollTop - clientHeight < 10;
      isUserAtBottomRef.current = isAtBottom;

      if (
        hasMore &&
        scrollTop < 3 &&
        !isLoadingOlderMessagesRef.current &&
        selectedRoom?.conversationId
      ) {
        isLoadingOlderMessagesRef.current = true;

        dispatch(
          fetchOlderMessagesForRoom({
            collaborationId: selectedCollaborationId,
            roomId: selectedRoomId,
            conversationId: selectedRoom?.conversationId,
            lastVisible,
          })
        );
      }

      if (scrollHeight) {
        setShowScrollBottom(scrollHeight - scrollTop >= 800);
      }
    },
    [
      hasMore,
      messages.length,
      selectedCollaborationId,
      selectedRoomId,
      selectedRoom?.conversationId,
      lastVisible,
      dispatch,
    ]
  );

  // Scroll to bottom
  const scrollToBottom = useCallback(() => {
    const chatBody = chatBodyRef.current;
    if (chatBody) {
      chatBody.scrollTop = chatBody.scrollHeight - chatBody.clientHeight;
    }
  }, []);

  const smoothScrollToBottom = useCallback(() => {
    const chatBody = chatBodyRef.current;
    if (chatBody) {
      chatBody.scrollTo({
        top: chatBody.scrollHeight - chatBody.clientHeight,
        behavior: "smooth",
      });
      setShowScrollBottom(false);
    }
  }, []);

  // Debug shortcut
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const isCmd = isMac ? event.metaKey : event.ctrlKey;

      if (isCmd && event.key.toLowerCase() === "d") {
        event.preventDefault();
        setDebug((prev) => !prev);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  // Scroll effects
  useEffect(() => {
    const roomChanged = previousRoomIdRef.current !== selectedRoomId;

    if (messages.length > 0 && chatBodyRef.current) {
      if (roomChanged || !hasInitiallyScrolledRef.current) {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            scrollToBottom();
            hasInitiallyScrolledRef.current = true;
            previousRoomIdRef.current = selectedRoomId;
          });
        });
      }
    }
  }, [messages.length, selectedRoomId, scrollToBottom]);

  useEffect(() => {
    if (previousRoomIdRef.current !== selectedRoomId) {
      hasInitiallyScrolledRef.current = false;
      isUserAtBottomRef.current = true;
      isLoadingOlderMessagesRef.current = false;
      previousMessageCountRef.current = 0;
    }
  }, [selectedRoomId]);

  // Reset loading flag when Redux isLoading changes to false
  useEffect(() => {
    if (!isLoading) {
      isLoadingOlderMessagesRef.current = false;
    }
  }, [isLoading]);

  // Reset "sending to platform" state after message send completes
  useEffect(() => {
    if (!isSending && isSendingToPlatform) {
      setIsSendingToPlatform(false);
      setUploadProgress(0);
      setEstimatedUploadTime(undefined);
    }
  }, [isSending, isSendingToPlatform]);

  // Warn before leaving the page while an upload is in progress (Bug 3)
  useEffect(() => {
    if (!isUploading && !isSendingToPlatform) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Modern browsers ignore the custom string but require returnValue to be set
      event.returnValue = 'Upload in progress. Leave page?';
      return 'Upload in progress. Leave page?';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isUploading, isSendingToPlatform]);

  // Per-room drafts: preserve message text + file when switching between rooms (Bug 5)
  const currentRoomKey =
    selectedCollaborationId && selectedRoomId
      ? `${selectedCollaborationId}_${selectedRoomId}`
      : null;

  type RoomDraft = {
    message: string;
    file?: File;
    fileSize: string;
    inputFileName: string;
    messageType: string | undefined;
    generatedThumbnail: string | null;
  };
  const draftsRef = useRef<Map<string, RoomDraft>>(new Map());
  const draftStateRef = useRef<RoomDraft>({
    message,
    file,
    fileSize,
    inputFileName,
    messageType,
    generatedThumbnail,
  });
  useEffect(() => {
    draftStateRef.current = {
      message,
      file,
      fileSize,
      inputFileName,
      messageType,
      generatedThumbnail,
    };
  });

  useEffect(() => {
    if (!currentRoomKey) return;

    // Restore this room's draft (if any) — otherwise start with an empty input
    const draft = draftsRef.current.get(currentRoomKey);
    setMessage(draft?.message ?? '');
    setFile(draft?.file);
    setFileSize(draft?.fileSize ?? '');
    setInputFileName(draft?.inputFileName ?? '');
    setMessageType(draft?.messageType ?? 'text');
    setGeneratedThumbnail(draft?.generatedThumbnail ?? null);

    // On room change / unmount, persist this room's latest draft
    const keyAtSetup = currentRoomKey;
    return () => {
      const latest = draftStateRef.current;
      if (latest.message.trim() !== '' || latest.file) {
        draftsRef.current.set(keyAtSetup, latest);
      } else {
        draftsRef.current.delete(keyAtSetup);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentRoomKey]);

  // Clear send error when the user switches rooms or starts a new draft
  useEffect(() => {
    setSendError(undefined);
  }, [currentRoomKey]);

  return (
    <MediaModalProvider>
      <div
        className="tw-flex tw-flex-1 tw-flex-col tw-w-full tw-min-h-0 tw-overflow-hidden tw-m-0 tw-p-0"
        id="kt_chat_messenger_body"
      >
        {/* Contact header */}
        <ChatContactHeader
          collaboration={selectedCollaboration}
          selectedRoom={selectedRoom}
          contactData={contactData}
          activeTab={activeTab}
          onBackClick={onBackClick}
          onAddNewContact={onAddNewContact}
          onAddExistingContact={onAddExistingContact}
          isMobile={isMobile}
        />

        {/* Message list */}
        <ChatMessageList
          messages={messages}
          collaboration={selectedCollaboration}
          isLoading={isLoading}
          hasMore={hasMore}
          debug={debug}
          onScroll={handleScroll}
          showScrollBottom={showScrollBottom}
          onScrollToBottom={smoothScrollToBottom}
        />

        {/* Input area */}
        <ChatInputArea
          message={message}
          file={file}
          inputFileName={inputFileName}
          fileSize={fileSize}
          generatedThumbnail={generatedThumbnail}
          messageType={messageType}
          isSending={isSending}
          setIsSending={setIsSending}
          replyToMessage={replyToMessage}
          orderInitiateMessage={orderInitiateMessage}
          sendError={sendError}
          uploadProgress={uploadProgress}
          isUploading={isUploading}
          isSendingToPlatform={isSendingToPlatform}
          estimatedUploadTime={estimatedUploadTime}
          collaboration={selectedCollaboration}
          companyId={String(selectedCollaboration?.company?.id)}
          channel={selectedRoom?.channel}
          shopId={account?.marketplaceShopId ?? roomAccountData?.marketplaceShopId}
          buyerId={selectedRoom?.userSearchKey}
          onMessageChange={setMessage}
          onFileSelect={handleFileSelect}
          onRemoveFile={handleRemoveFile}
          onSendMessage={sendMessage}
          onEnterPress={onEnterPress}
          onCancelUpload={handleCancelUpload}
        />

        {/* Single shared modal for all media in chat */}
        <MediaModal />

        {/* Data usage warning modal for cellular uploads */}
        <DataUsageWarningModal
          isOpen={showDataWarning}
          fileName={inputFileName}
          fileSize={fileSize}
          onContinue={handleDataWarningContinue}
          onCancel={handleDataWarningCancel}
        />
      </div>
    </MediaModalProvider>
  );
};

export default ChatInner;
